export const ACTIVATE_SERVICE_WORKER_REQUEST = "WHITEHAVEN_ACTIVATE_UPDATE";
export const ACTIVATE_SERVICE_WORKER_RESPONSE =
  "WHITEHAVEN_UPDATE_ACTIVATION_RESULT";
export const SERVICE_WORKER_UPDATE_PROTOCOL_VERSION = 1;
export const DEFAULT_ACTIVATION_ACK_TIMEOUT_MS = 4_000;
export const DEFAULT_CONTROLLER_CHANGE_TIMEOUT_MS = 10_000;

export type ServiceWorkerUpdateStatus =
  | "unsupported"
  | "idle"
  | "registering"
  | "checking"
  | "installing"
  | "ready"
  | "deferred"
  | "blocked"
  | "activating"
  | "refresh-required"
  | "error";

export type ServiceWorkerUpdateAction =
  | "activate"
  | "reload"
  | "review"
  | "retry";

export type ServiceWorkerUpdateCode =
  | "REGISTRATION_FAILED"
  | "UPDATE_CHECK_FAILED"
  | "UPDATE_INSTALL_FAILED"
  | "UNSAVED_WORK"
  | "OFFLINE"
  | "ACTIVATION_POST_FAILED"
  | "ACTIVATION_REJECTED"
  | "ACTIVATION_ACK_TIMEOUT"
  | "ACTIVATION_TIMEOUT"
  | "RELOAD_FAILED";

export type ServiceWorkerUpdateSource =
  | "existing-waiting"
  | "updatefound"
  | "controllerchange";

export interface ServiceWorkerUpdateSnapshot {
  readonly status: ServiceWorkerUpdateStatus;
  readonly action: ServiceWorkerUpdateAction | null;
  readonly code: ServiceWorkerUpdateCode | null;
  readonly blockerCount: number;
  readonly canFocusBlocker: boolean;
  readonly source: ServiceWorkerUpdateSource | null;
}

export interface ActivateServiceWorkerRequest {
  readonly type: typeof ACTIVATE_SERVICE_WORKER_REQUEST;
  readonly version: typeof SERVICE_WORKER_UPDATE_PROTOCOL_VERSION;
  readonly requestId: string;
}

export type ActivateServiceWorkerResponse =
  | {
      readonly type: typeof ACTIVATE_SERVICE_WORKER_RESPONSE;
      readonly version: typeof SERVICE_WORKER_UPDATE_PROTOCOL_VERSION;
      readonly requestId: string;
      readonly ok: true;
    }
  | {
      readonly type: typeof ACTIVATE_SERVICE_WORKER_RESPONSE;
      readonly version: typeof SERVICE_WORKER_UPDATE_PROTOCOL_VERSION;
      readonly requestId: string;
      readonly ok: false;
      readonly code: "ACTIVATION_FAILED";
    };

export const IDLE_SERVICE_WORKER_UPDATE: ServiceWorkerUpdateSnapshot =
  Object.freeze({
    status: "idle",
    action: null,
    code: null,
    blockerCount: 0,
    canFocusBlocker: false,
    source: null,
  });

const REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

export function isServiceWorkerUpdateRequestId(
  value: unknown,
): value is string {
  return typeof value === "string" && REQUEST_ID_PATTERN.test(value);
}

export function createServiceWorkerUpdateSnapshot(
  status: ServiceWorkerUpdateStatus,
  options: Partial<Omit<ServiceWorkerUpdateSnapshot, "status">> = {},
): ServiceWorkerUpdateSnapshot {
  return Object.freeze({
    status,
    action: options.action ?? null,
    code: options.code ?? null,
    blockerCount: options.blockerCount ?? 0,
    canFocusBlocker: options.canFocusBlocker ?? false,
    source: options.source ?? null,
  });
}

export function isActivateServiceWorkerMessage(
  value: unknown,
): value is ActivateServiceWorkerRequest {
  if (!value || typeof value !== "object") {
    return false;
  }

  const message = value as Record<string, unknown>;
  const keys = Object.keys(message);
  return (
    keys.length === 3 &&
    keys.every((key) => ["type", "version", "requestId"].includes(key)) &&
    message.type === ACTIVATE_SERVICE_WORKER_REQUEST &&
    message.version === SERVICE_WORKER_UPDATE_PROTOCOL_VERSION &&
    isServiceWorkerUpdateRequestId(message.requestId)
  );
}

export function isActivateServiceWorkerResponse(
  value: unknown,
): value is ActivateServiceWorkerResponse {
  if (!value || typeof value !== "object") {
    return false;
  }

  const message = value as Record<string, unknown>;
  const expectedKeys =
    message.ok === true
      ? ["type", "version", "requestId", "ok"]
      : ["type", "version", "requestId", "ok", "code"];
  const keys = Object.keys(message);

  return (
    keys.length === expectedKeys.length &&
    keys.every((key) => expectedKeys.includes(key)) &&
    message.type === ACTIVATE_SERVICE_WORKER_RESPONSE &&
    message.version === SERVICE_WORKER_UPDATE_PROTOCOL_VERSION &&
    isServiceWorkerUpdateRequestId(message.requestId) &&
    (message.ok === true ||
      (message.ok === false && message.code === "ACTIVATION_FAILED"))
  );
}

export function shouldShowServiceWorkerUpdate(
  snapshot: ServiceWorkerUpdateSnapshot,
): boolean {
  return [
    "ready",
    "blocked",
    "activating",
    "refresh-required",
    "error",
  ].includes(snapshot.status);
}
